import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Flex, Tooltip } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Container, Modal, Col, Row } from 'react-bootstrap'
import { useToasts } from '../../contexts/toasts'
import { useFullScreenLoader } from '../../contexts/fullScreenLoader'
import AlidaLoader from '../../components/common/AlidaLoader'
import { ApiKey, ApiKeys } from '../../api/ApiKeys'
import ApiKeyForm from '../../components/forms/ApiKeyForm'
import { useAuth } from '../../contexts/auth'
import IconButton from '../../components/common/IconButton'
import AppSearch from '../../components/common/AppSearch'
import { formatDate } from '../../utils'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { sectionLabelsSelector } from '../../slices/sectionsSlice'

export default function ApiKeysList() {
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const { loggedUser } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>();
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState(false);
  const [key, setKey] = useState<ApiKey>();
  const [editing, setEditing] = useState<number>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreatedKeyModal, setShowCreatedKeyModal] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState<ApiKey>();

  const assetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "api-keys"));

  const filteredKeys = useMemo(() => apiKeys?.filter(key => key.label.toLowerCase().includes(searchText.toLowerCase())), [apiKeys, searchText]);

  const initKeys = useCallback(() => {
    ApiKeys.getApiKeys()
      .then(
        ({ data: keys }) => setApiKeys(keys),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('api-keys-error', message, `${assetLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
          setError(true);
          setApiKeys([]);
        }
      )
  }, [showToast, assetLabels]);

  useEffect(initKeys, [initKeys]);

  const reset = useCallback(() => {
    setError(false);
    setApiKeys(undefined);
    initKeys();
  }, [initKeys]);

  const handleClose = useCallback(() => {
    setShowDeleteModal(false);
    setShowEditModal(false);
    setKey(undefined);
    setEditing(undefined);
  }, []);

  const handleCreatedKeyClose = useCallback(() => {
    setShowCreatedKeyModal(false);
    setCreatedApiKey(undefined);
    setKey(undefined);
  }, []);

  const openCreateModal = useCallback(() => setShowEditModal(true), []);

  const handleSubmit = useCallback((values: any) => {
    showLoader();

    ApiKeys.createApiKey({ ...values, user: loggedUser?.preferred_username })
      .then(
        ({ data }) => {
          hideLoader();
          showToast('api-key-success', `${assetLabels.singular} ${editing !== undefined ? 'updated' : 'created'} successfully`, `${assetLabels.singular} ${editing !== undefined ? 'Updated' : 'Created'}`, 'success');
          handleClose();
          setCreatedApiKey(data);
          setShowCreatedKeyModal(true);
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('api-key-error', message, `${assetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [handleClose, reset, showLoader, hideLoader, editing, showToast, loggedUser, assetLabels]);

  const handleDelete = useCallback((key: ApiKey) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setKey(key);
  }, []);

  const deleteApiKey = useCallback(() => {
    showLoader();

    ApiKeys.deleteApiKey(key!.label)
      .then(
        () => {
          hideLoader();
          handleClose();
          reset();
          showToast('api-keys-success', `${assetLabels.singular} deleted successfully`, `${assetLabels.singular} Success`, 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('api-key-error', message, `${assetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showLoader, hideLoader, handleClose, key, reset, showToast, assetLabels]);

  const copyToClipboard = useCallback(() => {
    if (createdApiKey?.apiKey) {
      navigator.clipboard.writeText(createdApiKey.apiKey);
      showToast('text-copied', 'Value copied to clipboard!');
    }
  }, [createdApiKey, showToast]);

  const renderApiKeys = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    return filteredKeys!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No API Key found.</span>
        </Alert>
      )
      : (
        <Row className="gx-2">
          {filteredKeys!.map((item, index) => (
            <Col key={index} md={4} sm={6}>
              <Card className="shadow-sm mb-3">
                <Card.Body className="p-2">
                  <Flex align="center">
                    <Col className="ms-2 overflow-hidden">
                      <div className="text-truncate">
                        <Tooltip title={item.label} mouseEnterDelay={0.5}>
                          <span>{item.label}</span>
                        </Tooltip>
                      </div>
                      <small className="text-muted">{formatDate(item.details.creation_time)}</small>
                    </Col>
                    <Flex className="gap-2 text-end">
                      <IconButton onClick={handleDelete(item)}>
                        <FontAwesomeIcon icon={solid('trash')} />
                      </IconButton>
                    </Flex>
                  </Flex>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )
  }, [filteredKeys, error, handleDelete]);

  const renderAsyncContent = useCallback(() => {
    return apiKeys
      ? renderApiKeys()
      : (
        <Flex justify="center">
          <AlidaLoader size={300} />
        </Flex>
      );
  }, [renderApiKeys, apiKeys]);

  return (
    <Container fluid="md" className="mt-3">
      <Row className="mb-3 align-items-center">
        <Col className="order-1">
          <h4 className="mb-0">{assetLabels.plural} List</h4>
        </Col>

        <Col md={5} className="order-3 order-md-2">
          <AppSearch placeholder={`Search ${assetLabels.singular}...`} value={searchText} onChange={setSearchText} />
        </Col>

        <Col className="order-2 order-md-3 text-end">
          <Button size="sm" variant="success" onClick={openCreateModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Create {assetLabels.singular}</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <Modal show={showEditModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Create {assetLabels.singular}</Modal.Title>
        </Modal.Header>
        <ApiKeyForm onSubmit={handleSubmit} onCancel={handleClose} />
      </Modal>

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {assetLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {assetLabels.singular} <b>{key?.label}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteApiKey}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreatedKeyModal} onHide={handleCreatedKeyClose} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>{assetLabels.singular} Created</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Here is the secret for <b>{createdApiKey?.label}</b> key:</p>

          <Flex align="center" className="border rounded bg-light mb-3 w-100">
            <Col className="p-2">
              <h5 className="m-0 text-break font-monospace text-secondary">{createdApiKey?.apiKey}</h5>
            </Col>
            <IconButton tooltip="Copy to Clipboard" className="p-3" onClick={copyToClipboard}>
              <FontAwesomeIcon icon={solid('clipboard')} />
            </IconButton>
          </Flex>

          <Alert variant="warning" className="py-2">
            <Flex align="center">
              <FontAwesomeIcon icon={solid('exclamation-triangle')} size="lg" />
              <span className="ms-3">Copy this {assetLabels.singular} in a safe place, you will not be able to see this secret again.</span>
            </Flex>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border mx-auto px-3" onClick={handleCreatedKeyClose}>
            <FontAwesomeIcon icon={solid('times')} />
            <span className="ms-2">Close</span>
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
